import { useEffect, useState } from 'react';

import { Button, Card, Tooltip, Typography } from 'antd';

import { formatDate } from 'helpers/date';
import { IMPACT_ANALYTICS_URL } from 'settings';
import { useImpactReport } from 'v2/hooks/useImpactReport';
import type { ImpactReportView } from 'v2/interfaces/impactMeter';
import { useUserStore } from 'v2/store/User';

import { Icon } from '@eduzz/ui-antd-theme';

import CalculationModal from './CalculationModal';
import { IMPACT_CALCULATION_TITLE } from './constants';
import DiplomaModal from './DiplomaModal';
import { formatCompactImpact } from './formatImpact';
import GrafismoBackground from './GrafismoBackground';
import ImpactMetrics from './ImpactMetrics';
import RadarDecoration from './RadarDecoration';
import { useImpactMotion } from './useImpactMotion';

const IMPACT_CARD_CLASS = 'mt-4 overflow-hidden border-none bg-[linear-gradient(90deg,#0F1324_0%,#1B2340_100%)]';
const IMPACT_DATE_CLASS = 'text-xs leading-4 whitespace-nowrap text-(--uizz-antd-color-white) opacity-80';
const IMPACT_RADAR_MEDIA = '(min-width: 1024px)';

function useIsLargeScreen() {
  const [isLargeScreen, setIsLargeScreen] = useState(() => window.matchMedia(IMPACT_RADAR_MEDIA).matches);

  useEffect(() => {
    const media = window.matchMedia(IMPACT_RADAR_MEDIA);
    const onChange = () => {
      setIsLargeScreen(media.matches);
    };

    onChange();
    media.addEventListener('change', onChange);

    return () => {
      media.removeEventListener('change', onChange);
    };
  }, []);

  return isLargeScreen;
}

function UpdatedAt({ value, className }: { value: string; className?: string }) {
  if (!value) {
    return null;
  }

  return (
    <Typography.Text className={`${IMPACT_DATE_CLASS} ${className ?? ''}`.trim()}>Dados até {value}</Typography.Text>
  );
}

type ImpactMeterContentProps = {
  report: ImpactReportView;
  defaultName: string;
  userId: number;
};

function ImpactMeterContent({ report, defaultName, userId }: ImpactMeterContentProps) {
  const isLargeScreen = useIsLargeScreen();
  const { cardRef, parallaxRef, warpRef, radarRef, counterSpinRefs } = useImpactMotion(isLargeScreen);
  const [metricsOpen, setMetricsOpen] = useState(false);
  const [calculationOpen, setCalculationOpen] = useState(false);
  const [diplomaOpen, setDiplomaOpen] = useState(false);
  const formattedDate = formatDate(report.updatedAt);

  return (
    <>
      <Card
        id='impact-meter'
        className={IMPACT_CARD_CLASS}
        classNames={{ body: 'relative overflow-hidden p-0' }}
        aria-labelledby='impact-meter-title'
      >
        <div ref={cardRef} className='relative flex flex-col gap-4 px-5 py-4'>
          <GrafismoBackground parallaxRef={parallaxRef} warpRef={warpRef} />

          <div className='relative flex flex-wrap items-end gap-x-5 gap-y-3'>
            <div className='flex w-full flex-col items-start gap-1 lg:w-auto lg:min-w-120'>
              <div className='flex w-full items-center gap-2'>
                <Icon.Radar size={24} className='shrink-0 text-(--uizz-antd-color-warning)' aria-hidden />
                <Typography.Text
                  id='impact-meter-title'
                  className='text-[11px] leading-4 font-semibold tracking-[0.6px] whitespace-nowrap text-(--uizz-antd-color-white) uppercase opacity-80'
                >
                  Impactômetro
                </Typography.Text>
                <Tooltip title={IMPACT_CALCULATION_TITLE}>
                  <Button
                    type='text'
                    size='small'
                    id='impact-meter-info'
                    aria-label={IMPACT_CALCULATION_TITLE}
                    aria-haspopup='dialog'
                    aria-expanded={calculationOpen}
                    aria-controls='impact-meter-calculation'
                    className='size-5 min-w-0 p-0 text-(--uizz-antd-color-white) opacity-70 hover:bg-transparent hover:text-(--uizz-antd-color-white) hover:opacity-100'
                    icon={<Icon.Info size={14} aria-hidden />}
                    onClick={() => setCalculationOpen(true)}
                  />
                </Tooltip>
              </div>

              <div className='flex w-full flex-col gap-2 lg:flex-row lg:items-center lg:gap-5'>
                <div className='flex w-full items-center gap-2 lg:w-auto'>
                  <Typography.Text className='leading-0.95 text-[56px] font-extrabold tracking-[-2px] text-(--uizz-antd-color-white) lg:text-7xl'>
                    {formatCompactImpact(report.estimatedImpactCount)}
                  </Typography.Text>
                  <Button
                    type='text'
                    id='impact-meter-metrics-toggle'
                    aria-expanded={metricsOpen}
                    aria-controls='impact-meter-metrics'
                    aria-label='Ver indicadores'
                    className='ml-auto size-11 shrink-0 bg-transparent text-(--uizz-antd-color-white) hover:bg-white/10 hover:text-(--uizz-antd-color-white) lg:hidden'
                    icon={
                      <Icon.ChevronDown
                        size={20}
                        aria-hidden
                        className={`transition-transform ${metricsOpen ? 'rotate-180' : ''}`}
                      />
                    }
                    onClick={() => setMetricsOpen(open => !open)}
                  />
                </div>
                <Typography.Text className='text-sm leading-5 font-medium text-balance text-(--uizz-antd-color-white) sm:max-w-60 sm:text-lg sm:leading-6'>
                  pessoas impactadas com a Eduzz.
                </Typography.Text>
              </div>
            </div>

            {isLargeScreen ? <RadarDecoration radarRef={radarRef} counterSpinRefs={counterSpinRefs} /> : null}

            <div className='flex w-full shrink-0 flex-col gap-1 lg:w-auto lg:justify-between lg:self-stretch'>
              <UpdatedAt value={formattedDate} className='hidden self-end lg:block' />
              <div className='flex w-full flex-wrap gap-2 lg:flex-col lg:flex-nowrap'>
                <Button
                  id='impact-meter-analytics'
                  color='gold'
                  variant='solid'
                  size='small'
                  href={IMPACT_ANALYTICS_URL}
                  className='min-w-0 flex-1 lg:flex-none'
                >
                  Ver meu impacto
                  <Icon.ArrowRight size={14} className='hidden sm:inline' aria-hidden />
                </Button>
                <Button
                  type='text'
                  size='small'
                  id='impact-meter-diploma-trigger'
                  aria-haspopup='dialog'
                  aria-expanded={diplomaOpen}
                  aria-controls='impact-meter-diploma'
                  className='w-11 shrink-0 bg-transparent px-0 text-(--uizz-antd-color-white) hover:bg-white/10 hover:text-(--uizz-antd-color-white) sm:w-auto sm:flex-1 sm:px-2 lg:flex-none'
                  icon={<Icon.Download size={16} aria-hidden />}
                  onClick={() => setDiplomaOpen(true)}
                >
                  <span className='sr-only sm:not-sr-only sm:inline'>Baixar diploma</span>
                </Button>
              </div>
            </div>
          </div>

          <div
            id='impact-meter-metrics'
            className={`relative flex w-full flex-col gap-2 ${metricsOpen ? '' : 'hidden'} lg:flex`}
          >
            <UpdatedAt value={formattedDate} className='self-end lg:hidden' />
            <ImpactMetrics report={report} />
          </div>
        </div>
      </Card>

      <CalculationModal open={calculationOpen} onClose={() => setCalculationOpen(false)} />
      <DiplomaModal
        open={diplomaOpen}
        onClose={() => setDiplomaOpen(false)}
        userId={userId}
        defaultName={defaultName}
        report={report}
      />
    </>
  );
}

function ImpactMeter() {
  const user = useUserStore(state => state.user);
  const { data: report } = useImpactReport(user?.id);

  if (!user?.id || !report) {
    return null;
  }

  return (
    <ImpactMeterContent
      report={report}
      userId={report.customerId ?? user.id}
      defaultName={report.customerName || user.business_name || user.full_name || ''}
    />
  );
}

export default ImpactMeter;
